import { post } from "./auth-request.ts";
import { grantFrom, type Grant } from "./auth.ts";

/**
 * Resuming a kept device (P1.4-T7, ADR-075 decision 3). A tab that holds no grant asks the gateway whether this
 * browser is kept logged in: the request carries the kept session's HttpOnly cookie, which no script here can
 * read, and the answer is a tab's grant as a login's with the account's email, which the returning prompt
 * shows before anything joins.
 */

/**
 * What asking to resume came to: the grant and the email to show; `refused`, the gateway's 401, which is how it
 * says this browser is not kept (no cookie, or one that can no longer resume, which it has cleared); or
 * `failed`, for no answer, a server error, a limit or a body the page cannot read, after which the cookie is
 * left as it was and the next visit asks again.
 */
export type ResumeAttempt = { readonly kind: "granted"; readonly grant: Grant; readonly email: string } | { readonly kind: "refused" } | { readonly kind: "failed" };

/**
 * Asks the gateway to resume the kept session this browser holds, if any.
 *
 * @param base The account origin.
 * @returns What it came to.
 */
export async function resume(base: string): Promise<ResumeAttempt> {
  const answer = await post(base, "resume", {}, true);
  if (!answer.ok) {
    return answer.status === 401 ? { kind: "refused" } : { kind: "failed" };
  }
  const grant = grantFrom(answer.body);
  const email = typeof answer.body === "object" && answer.body !== null ? (answer.body as { email?: unknown }).email : undefined;
  return grant === null || typeof email !== "string" || email === "" ? { kind: "failed" } : { kind: "granted", grant, email };
}
